import {
  AssessmentOutlined,
  BarChartOutlined,
  CalendarMonthOutlined,
  ChecklistOutlined,
  GroupsOutlined,
  PaidOutlined,
  PeopleOutlined,
  ReceiptLongOutlined,
  ShowChartOutlined,
  TimelineOutlined,
} from '@mui/icons-material';
import { Box } from '@mui/material';
import { TodoWidget } from 'src/modules/dashboard/components/todoWidget';
import { MemberChartV2 } from 'src/modules/dashboard/components/members/memberChartV2';
import { MemberDashboardLog } from 'src/modules/dashboard/components/members/memberDashboardLog';
import { MembersStatsWidget } from './widgets/membersStatsWidget';
import { ContactsChartWidget } from './widgets/contactsChartWidget';
import { HouseholdMemberWidget, MainMemberWidget } from './widgets/memberTypeWidgets';
import { AutojobsWidget } from './widgets/autojobsWidget';
import { RealMembersByProfileWidget } from './widgets/realMembersByProfileWidget';
import { GiftsByPeriodWidget } from './widgets/giftsByPeriodWidget';
import { NewMembersByPeriodWidget } from './widgets/newMembersByPeriodWidget';
import { useDashboardFilter } from './dashboardFilterContext';
import { WidgetDefinition } from './types';

const ContextualMemberChart = () => {
  const { associationId, districtId } = useDashboardFilter();
  return <MemberChartV2 associationId={associationId} districtId={districtId} />;
};

const ContextualMemberLog = () => {
  const { associationId, districtId } = useDashboardFilter();
  return (
    <Box sx={{ height: '100%', overflowY: 'auto' }}>
      <MemberDashboardLog associationId={associationId} districtId={districtId} />
    </Box>
  );
};

export const WIDGET_CATALOG: WidgetDefinition[] = [
  {
    id: 'real-members-by-profile',
    category: 'kpi',
    module: 'memberReg',
    name: 'Medlemmar per profil',
    description: 'Antal medlemmar med valda statusar och profil-items',
    iconName: 'Groups',
    defaultW: 4,
    defaultH: 6,
    minW: 3,
    minH: 4,
    configurable: true,
    defaultConfig: { statusKeys: ['paid', 'notPaid'], profileItemIds: [] },
    render: (instance) => <RealMembersByProfileWidget instance={instance} />,
  },
  {
    id: 'real-members-stats',
    category: 'kpi',
    module: 'memberReg',
    name: 'Medlemmar',
    description: 'Total + betalda/obetalda/intresse',
    iconName: 'People',
    defaultW: 5,
    defaultH: 8,
    minW: 3,
    minH: 6,
    render: () => <MembersStatsWidget />,
  },
  {
    id: 'real-contacts',
    category: 'kpi',
    module: 'memberReg',
    name: 'Kontaktvägar',
    description: 'E-post, mobilnummer, saknas',
    iconName: 'Receipt',
    defaultW: 5,
    defaultH: 8,
    minW: 3,
    minH: 6,
    render: () => <ContactsChartWidget />,
  },
  {
    id: 'real-main-member',
    category: 'kpi',
    module: 'memberReg',
    name: 'Huvudmedlem',
    description: 'Antal huvudmedlemmar',
    iconName: 'People',
    defaultW: 2,
    defaultH: 3,
    minW: 2,
    minH: 2,
    render: () => <MainMemberWidget />,
  },
  {
    id: 'real-household-member',
    category: 'kpi',
    module: 'memberReg',
    name: 'Hushållsmedlem',
    description: 'Antal hushållsmedlemmar',
    iconName: 'People',
    defaultW: 2,
    defaultH: 3,
    minW: 2,
    minH: 2,
    render: () => <HouseholdMemberWidget />,
  },
  {
    id: 'real-autojobs',
    category: 'system',
    module: 'memberReg',
    name: 'Automatiska jobb',
    description: 'Senaste och kommande schemalagda jobb',
    iconName: 'Default',
    defaultW: 6,
    defaultH: 8,
    minW: 4,
    minH: 6,
    render: () => <AutojobsWidget />,
  },
  {
    id: 'real-todo',
    category: 'list',
    module: 'memberReg',
    name: 'Att göra',
    description: 'Påminnelser och uppgifter',
    iconName: 'Checklist',
    defaultW: 4,
    defaultH: 8,
    minW: 3,
    minH: 6,
    render: () => <TodoWidget />,
  },
  {
    id: 'real-member-log',
    category: 'list',
    module: 'memberReg',
    name: 'Senaste medlemshändelser',
    description: 'Ändringshistorik på medlemmar',
    iconName: 'Timeline',
    defaultW: 8,
    defaultH: 10,
    minW: 4,
    minH: 6,
    render: () => <ContextualMemberLog />,
  },
  {
    id: 'real-member-chart',
    category: 'chart',
    module: 'memberReg',
    name: 'Medlemmar per månad',
    description: 'Stapeldiagram över paid/unpaid över tid',
    iconName: 'ShowChart',
    defaultW: 7,
    defaultH: 10,
    minW: 4,
    minH: 8,
    render: () => <ContextualMemberChart />,
  },
  {
    id: 'real-new-members-by-period',
    category: 'kpi',
    module: 'memberReg',
    name: 'Nya medlemmar',
    description: 'Antal nya medlemmar under vald period',
    iconName: 'People',
    defaultW: 4,
    defaultH: 6,
    minW: 3,
    minH: 4,
    configurable: true,
    defaultConfig: { period: 'thisMonth' },
    render: (instance) => <NewMembersByPeriodWidget instance={instance} />,
  },
  {
    id: 'real-gifts-by-period',
    category: 'kpi',
    module: 'fundraiser',
    name: 'Gåvor för period',
    description: 'Totalt belopp av gåvor under vald period',
    iconName: 'Paid',
    defaultW: 4,
    defaultH: 6,
    minW: 3,
    minH: 4,
    configurable: true,
    defaultConfig: { period: 'thisMonth' },
    render: (instance) => <GiftsByPeriodWidget instance={instance} />,
  },
];

export const MODULE_LABELS: Record<string, string> = {
  memberReg: 'Medlemsregister',
  invoice: 'Fakturering',
  fundraiser: 'Gåvor',
  event: 'Evenemang',
};

export const WIDGET_ICONS: Record<string, React.ReactNode> = {
  People: <PeopleOutlined fontSize='small' />,
  Groups: <GroupsOutlined fontSize='small' />,
  BarChart: <BarChartOutlined fontSize='small' />,
  ShowChart: <ShowChartOutlined fontSize='small' />,
  Calendar: <CalendarMonthOutlined fontSize='small' />,
  Timeline: <TimelineOutlined fontSize='small' />,
  Checklist: <ChecklistOutlined fontSize='small' />,
  Receipt: <ReceiptLongOutlined fontSize='small' />,
  Paid: <PaidOutlined fontSize='small' />,
  Default: <AssessmentOutlined fontSize='small' />,
};

export const findWidget = (id: string): WidgetDefinition | undefined => WIDGET_CATALOG.find((w) => w.id === id);

export const CATEGORY_LABELS: Record<string, string> = {
  kpi: 'KPI:er',
  chart: 'Diagram',
  list: 'Listor',
  system: 'System',
};
